<template>
    <div class="one-c-import-page">
        <header class="one-c-import-header">
            <div>
                <button type="button" class="one-c-import-back" @click="goBack">
                    <i class="fas fa-arrow-left" aria-hidden="true"></i>
                    К сотрудникам
                </button>
                <h1>Импорт сотрудников из 1С:ЗУП</h1>
                <p>Предварительная проверка и перенос кадровых данных в текущую компанию Аргуса</p>
            </div>
            <b-badge variant="light" class="one-c-import-header__tenant">
                <i class="fas fa-building mr-1" aria-hidden="true"></i>
                Только текущая компания
            </b-badge>
        </header>

        <b-alert show variant="info" class="one-c-import-scope">
            <strong>Что переносится:</strong>
            штатные сотрудники с действующими трудовыми отношениями и все договоры ГПХ:
            ФИО, дата рождения, табельный номер, должность и дата приёма,
            официальное подразделение 1С, вид занятости и рабочий телефон.
            Завершённые штатные периоды откладываются. Договоры ГПХ сохраняются
            вместе с историей; окончание договора не увольняет сотрудника.
            После подтверждённого сопоставления официальные ФИО и дата рождения
            обновляются по данным 1С.
            Рабочий отдел Аргуса этим импортом не меняется. Зарплата, оклады и начисления
            не импортируются.
        </b-alert>

        <b-alert
            v-if="errorMessage"
            show
            dismissible
            variant="danger"
            @dismissed="errorMessage = ''"
        >
            {{ errorMessage }}
        </b-alert>

        <section v-if="applyResult" class="one-c-import-result">
            <div class="one-c-import-result__icon">
                <i class="fas fa-check" aria-hidden="true"></i>
            </div>
            <div>
                <h2>Импорт завершён</h2>
                <p>
                    Пакет №{{ applyResult.batch_id || '—' }} применён к текущей компании.
                    Исходный файл не сохранялся в Аргусе.
                </p>
                <div class="one-c-import-summary">
                    <article>
                        <span>Строк обработано</span>
                        <strong>{{ resultSummary.total || 0 }}</strong>
                    </article>
                    <article>
                        <span>Сопоставлено</span>
                        <strong>{{ resultSummary.matched || 0 }}</strong>
                    </article>
                    <article>
                        <span>Создано сотрудников</span>
                        <strong>{{ resultSummary.created || 0 }}</strong>
                    </article>
                    <article>
                        <span>Пропущено</span>
                        <strong>{{ resultSummary.ignored || 0 }}</strong>
                    </article>
                    <article>
                        <span>Трудовых периодов</span>
                        <strong>{{ resultSummary.employment_periods_created || 0 }}</strong>
                    </article>
                    <article>
                        <span>Контактов</span>
                        <strong>{{ resultSummary.contacts_created || 0 }}</strong>
                    </article>
                </div>
                <div class="one-c-import-result__actions">
                    <b-button v-if="resultSummary.deferred_employees" variant="warning" :to="{name: 'employee-merge-review'}">
                        Требует сверки: {{ resultSummary.deferred_employees }} сотрудников
                    </b-button>
                    <b-button variant="primary" @click="startAnotherImport">
                        Импортировать другой файл
                    </b-button>
                    <b-button variant="outline-secondary" @click="openBoard">
                        Открыть доску сотрудников
                    </b-button>
                </div>
            </div>
        </section>

        <template v-else>
            <section class="one-c-import-section">
                <div class="one-c-import-section__heading">
                    <span class="one-c-import-step">1</span>
                    <div>
                        <h2>Выгрузка из 1С</h2>
                        <p>Выберите сохранённый XLSX-файл. На этом шаге база не изменяется.</p>
                    </div>
                </div>

                <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    class="d-none"
                    @change="onFileSelected"
                >

                <div class="one-c-import-file">
                    <div class="one-c-import-file__info">
                        <i class="fas fa-file-excel" aria-hidden="true"></i>
                        <div>
                            <strong>{{ selectedFile ? selectedFile.name : 'Файл не выбран' }}</strong>
                            <span v-if="selectedFile">{{ formatFileSize(selectedFile.size) }}</span>
                            <span v-else>Поддерживается только формат XLSX</span>
                        </div>
                    </div>
                    <b-button variant="outline-primary" :disabled="busy" @click="chooseFile">
                        {{ selectedFile ? 'Выбрать другой файл' : 'Выбрать XLSX' }}
                    </b-button>
                    <b-button
                        v-if="selectedFile"
                        variant="primary"
                        :disabled="busy"
                        @click="previewSelectedFile"
                    >
                        <b-spinner v-if="previewLoading" small class="mr-1"></b-spinner>
                        <i v-else class="fas fa-search mr-1" aria-hidden="true"></i>
                        {{ preview ? 'Проверить заново' : 'Проверить файл' }}
                    </b-button>
                </div>
            </section>

            <template v-if="preview">
                <b-alert v-if="isGphImport" show variant="info" class="mt-3">
                    Договоры ГПХ на {{ formatDate(preview.reference_date) }}:
                    действуют — {{ scopeSummary.active_contracts || 0 }},
                    завершены — {{ scopeSummary.expired_contracts || 0 }},
                    будущие — {{ scopeSummary.future_contracts || 0 }}.
                    Новые сотрудники только с завершёнными договорами попадут в резерв,
                    только с будущими — в «Требуется сверка». Активация существующих — по подтверждению.
                    <div v-if="preview.metadata.skipped_record_count" class="mt-1">
                        Пропущено непроведённых или помеченных на удаление документов:
                        {{ preview.metadata.skipped_record_count }}.
                        Строки файла: {{ preview.metadata.skipped_records.map(item => item.row_number).join(', ') }}.
                    </div>
                </b-alert>
                <section class="one-c-import-section">
                    <div class="one-c-import-section__heading">
                        <span class="one-c-import-step one-c-import-step--done">
                            <i class="fas fa-check" aria-hidden="true"></i>
                        </span>
                        <div>
                            <h2>Результат предварительной проверки</h2>
                            <p>
                                Файл прочитан, но изменения ещё не применены.
                                Контрольная сумма: {{ shortHash(preview.file_sha256) }}
                            </p>
                        </div>
                    </div>

                    <div class="one-c-import-summary">
                        <article>
                            <span>{{ isGphImport ? 'Людей / договоров' : 'Действующих / строк' }}</span>
                            <strong>
                                {{ previewSummary.unique_employees || 0 }}
                                /
                                {{ previewSummary.total || 0 }}
                            </strong>
                        </article>
                        <article class="one-c-import-summary__success">
                            <span>Сопоставлены</span>
                            <strong>{{ previewSummary.employees_matched || 0 }}</strong>
                        </article>
                        <article class="one-c-import-summary__danger">
                            <span>Нужна проверка</span>
                            <strong>{{ previewSummary.employees_conflict || 0 }}</strong>
                        </article>
                        <article class="one-c-import-summary__warning">
                            <span>Не найдены</span>
                            <strong>{{ previewSummary.employees_unmatched || 0 }}</strong>
                        </article>
                        <article>
                            <span>Дубли строк</span>
                            <strong>{{ previewSummary.duplicate_source || 0 }}</strong>
                        </article>
                    </div>
                    <b-alert v-if="!isGphImport" show variant="secondary" class="mt-3 mb-0">
                        <strong>История отложена:</strong>
                        {{ scopeSummary.deferred_records || 0 }} завершённых строк
                        по {{ scopeSummary.deferred_record_employees || 0 }} людям.
                        Из них {{ scopeSummary.historical_only_employees || 0 }}
                        сотрудников без действующих трудовых отношений.
                        Они не участвуют в текущем сопоставлении и не будут импортированы.
                    </b-alert>
                </section>

                <section class="one-c-import-section">
                    <div class="one-c-import-section__heading">
                        <span
                            class="one-c-import-step one-c-import-step--optional"
                        >
                            <i class="fas fa-info" aria-hidden="true"></i>
                        </span>
                        <div>
                            <h2>
                                Подразделения 1С и отделы Аргуса
                                <b-badge variant="light" class="ml-1">Необязательно</b-badge>
                            </h2>
                            <p>
                                Официальное подразделение сохранится в данных 1С отдельно.
                                Сопоставление ниже — только справочная подсказка и не меняет
                                рабочий отдел сотрудника в Аргусе.
                            </p>
                        </div>
                    </div>

                    <b-alert v-if="!sourceDepartments.length" show variant="secondary">
                        В выгрузке нет официальных подразделений. Импорт можно продолжить.
                    </b-alert>

                    <template v-else>
                        <div class="one-c-import-mapping-toolbar">
                            <b-input-group size="sm">
                                <b-input-group-prepend is-text>
                                    <i class="fas fa-search" aria-hidden="true"></i>
                                </b-input-group-prepend>
                                <b-form-input
                                    v-model.trim="departmentQuery"
                                    type="search"
                                    placeholder="Найти подразделение 1С"
                                ></b-form-input>
                            </b-input-group>
                            <span
                                class="one-c-import-progress-label"
                            >
                                Справочно сопоставлено:
                                <strong>{{ mappedDepartmentCount }} из {{ sourceDepartments.length }}</strong>
                            </span>
                        </div>

                        <div class="one-c-import-mapping-list">
                            <div
                                v-for="sourceName in visibleSourceDepartments"
                                :key="sourceName"
                                class="one-c-import-mapping-row"
                            >
                                <div>
                                    <span>Подразделение 1С</span>
                                    <strong>{{ sourceName }}</strong>
                                </div>
                                <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
                                <div>
                                    <label :for="mappingControlId(sourceName)">Отдел Аргуса</label>
                                    <b-form-select
                                        :id="mappingControlId(sourceName)"
                                        v-model="departmentSelections[sourceName]"
                                        :options="departmentOptions"
                                        size="sm"
                                        :state="null"
                                        :disabled="mappingSaving || previewLoading"
                                        @change="onMappingChanged"
                                    ></b-form-select>
                                </div>
                            </div>
                        </div>

                        <div class="one-c-import-section__footer">
                            <span v-if="mappingsDirty" class="text-warning">
                                Есть несохранённые сопоставления. Они не блокируют импорт.
                            </span>
                            <span v-else-if="mappedDepartmentCount" class="text-success">
                                Справочные сопоставления сохранены
                            </span>
                            <span v-else class="text-success">
                                Этот шаг можно пропустить
                            </span>
                            <b-button
                                variant="primary"
                                :disabled="!canSaveMappings"
                                @click="saveMappings"
                            >
                                <b-spinner v-if="mappingSaving" small class="mr-1"></b-spinner>
                                Сохранить сопоставления
                            </b-button>
                        </div>
                    </template>
                </section>

                <section class="one-c-import-section">
                    <div class="one-c-import-section__heading">
                        <span
                            class="one-c-import-step"
                            :class="{'one-c-import-step--done': decisionsReady}"
                        >
                            <i v-if="decisionsReady" class="fas fa-check" aria-hidden="true"></i>
                            <template v-else>3</template>
                        </span>
                        <div>
                            <h2>Сопоставление сотрудников</h2>
                            <p>
                                Автоматические совпадения будут перенесены без ручного решения.
                                Для остальных выберите действие. Массовое связывание учитывает все строки файла и договоры человека.
                            </p>
                        </div>
                    </div>

                    <div class="one-c-import-review-toolbar">
                        <b-input-group size="sm" class="one-c-import-review-search">
                            <b-input-group-prepend is-text>
                                <i class="fas fa-search" aria-hidden="true"></i>
                            </b-input-group-prepend>
                            <b-form-input
                                v-model.trim="rowQuery"
                                type="search"
                                placeholder="ФИО, табельный номер, подразделение"
                            ></b-form-input>
                        </b-input-group>
                        <b-form-select
                            v-model="rowFilter"
                            :options="rowFilterOptions"
                            size="sm"
                        ></b-form-select>
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            :disabled="busy || bulkMatchConfirming || !bulkMatchPlan.row_count"
                            @click="bulkMatchEmployees"
                        >
                            Связать по ФИО и инициалам ({{ bulkMatchPlan.people_count }})
                        </b-button>
                        <b-dropdown
                            size="sm"
                            variant="outline-secondary"
                            text="Массовое действие"
                            :disabled="busy || bulkMatchConfirming"
                        >
                            <b-dropdown-item-button :disabled="!deferPlan.length" @click="bulkDefer">
                                Нерешённые — импортировать и отправить на сверку ({{ deferPlan.length }})
                            </b-dropdown-item-button>
                            <b-dropdown-item-button
                                :disabled="!statusCount('unmatched')"
                                @click="bulkResolve('unmatched', 'create')"
                            >
                                Несопоставленные — создать карточки
                            </b-dropdown-item-button>
                            <b-dropdown-item-button
                                :disabled="!statusCount('duplicate_source')"
                                @click="bulkResolve('duplicate_source', 'ignore')"
                            >
                                Дубли — игнорировать
                            </b-dropdown-item-button>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item-button @click="resetManualDecisions">
                                Сбросить ручные решения
                            </b-dropdown-item-button>
                        </b-dropdown>
                        <span
                            class="one-c-import-progress-label"
                            :class="{'text-danger': unresolvedRowCount}"
                        >
                            Нужны решения: <strong>{{ unresolvedRowCount }}</strong>
                        </span>
                    </div>

                    <div v-if="!filteredReviewRows.length" class="one-c-import-empty">
                        <i class="fas fa-check-circle" aria-hidden="true"></i>
                        <strong>Строк по выбранному фильтру нет</strong>
                    </div>

                    <div v-else class="one-c-import-table-wrap">
                        <table class="table table-sm one-c-import-table">
                            <thead>
                                <tr>
                                    <th>Строка 1С</th>
                                    <th>Результат проверки</th>
                                    <th>Решение кадровика</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in pagedReviewRows" :key="row.decision_key">
                                    <td>
                                        <div class="one-c-import-person">
                                            <strong>{{ row.source_display_name || 'Без имени' }}</strong>
                                            <span v-if="rowPersonnelNumber(row)">
                                                Таб. № {{ rowPersonnelNumber(row) }}
                                            </span>
                                            <span v-if="rowBirthDate(row)">
                                                Дата рождения: {{ formatDate(rowBirthDate(row)) }}
                                            </span>
                                            <span v-if="rowPosition(row)">{{ rowPosition(row) }}</span>
                                            <span v-if="rowSourceDepartment(row)">
                                                {{ rowSourceDepartment(row) }}
                                            </span>
                                            <span v-if="rowEmploymentPeriod(row)">
                                                {{ rowEmploymentPeriod(row) }}
                                            </span>
                                            <span v-if="rowPhone(row)">
                                                Телефон: {{ rowPhone(row) }}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <b-badge :variant="statusVariant(row.status)">
                                            {{ statusLabel(row.status) }}
                                        </b-badge>
                                        <div v-if="row.matched_employee" class="one-c-import-match">
                                            <span>Найден:</span>
                                            <strong>{{ candidateLabel(row.matched_employee) }}</strong>
                                        </div>
                                        <employee-match-candidates :candidates="row.match_candidates || row.candidates || []"
                                            v-model="decisionFor(row).reject_candidate_ids"
                                            :selected-id="decisionFor(row).action === 'match' ? decisionFor(row).employee_id : null"
                                            :editable="row.status !== 'duplicate_source' && decisionFor(row).action !== 'ignore'"
                                            :disabled="applying" @input="applyConfirmed = false" />
                                        <ul
                                            v-if="rowMessages(row).length"
                                            class="one-c-import-issues"
                                        >
                                            <li
                                                v-for="(message, index) in rowMessages(row)"
                                                :key="`${row.decision_key}-issue-${index}`"
                                            >
                                                {{ message }}
                                            </li>
                                        </ul>
                                    </td>
                                    <td>
                                        <template v-if="row.status === 'matched'">
                                            <div class="one-c-import-auto-decision">
                                                <i class="fas fa-link" aria-hidden="true"></i>
                                                Обновить найденного сотрудника
                                            </div>
                                        </template>
                                        <b-alert v-if="decisionFor(row).action === 'defer'" show variant="warning" class="mt-2">
                                            Будет создана отдельная карточка «Требует сверки». Все её договоры сохранятся;
                                            похожие карточки можно будет сравнить и объединить после импорта.
                                        </b-alert>
                                        <div v-if="row.status !== 'duplicate_source' && !['ignore', 'defer'].includes(decisionFor(row).action)"
                                             class="mt-2">
                                            <small v-if="needsEmployeeReview(row)" class="d-block text-muted" role="status">
                                                <template v-if="previewLoading || employeeReviewScheduled">
                                                    <b-spinner small class="mr-1" />Проверяем выбранного сотрудника…
                                                </template>
                                                <template v-else>Проверка не завершена. Повторите проверку файла.</template>
                                            </small>
                                            <template v-if="!needsEmployeeReview(row)">
                                            <small class="d-block">
                                                Форма найма: {{ hiringFormLabel(row.hiring_form_preview && row.hiring_form_preview.current) }}.
                                                По 1С: {{ row.hiring_form_preview && row.hiring_form_preview.requires_choice
                                                    ? 'Штат и ГПХ — выберите вручную' : hiringFormLabel(row.hiring_form_preview && row.hiring_form_preview.proposed) }}
                                            </small>
                                            <small v-if="row.lifecycle_preview" class="d-block">
                                                {{ lifecyclePreviewText(row) }}
                                            </small>
                                            <b-form-select v-model="decisionFor(row).hiring_form" size="sm"
                                                :options="hiringFormChoices" :disabled="applying"
                                                aria-label="Решение по форме найма" @change="applyConfirmed = false" />
                                            <b-form-checkbox v-if="row.hiring_form_preview && row.hiring_form_preview.manual_preserved && row.hiring_form_preview.proposed"
                                                v-model="decisionFor(row).accept_hiring_form_from_1c" :disabled="applying"
                                                @change="applyConfirmed = false">
                                                Принять из 1С вместо ручного значения
                                            </b-form-checkbox>
                                            <b-form-checkbox v-if="row.activation_available || (isGphImport && decisionFor(row).action === 'match')"
                                                v-model="decisionFor(row).activate_employee" :disabled="applying"
                                                @change="applyConfirmed = false">
                                                Активировать при действующем ГПХ, если сотрудник в резерве / требует сверки
                                            </b-form-checkbox>
                                            <b-form-checkbox v-if="decisionFor(row).activate_employee && row.manual_termination_event_id"
                                                v-model="decisionFor(row).confirmed_manual_termination_event_id"
                                                :value="row.manual_termination_event_id" :unchecked-value="null"
                                                :disabled="applying" @change="applyConfirmed = false">
                                                Подтверждаю возобновление отношений после ручного увольнения
                                            </b-form-checkbox>
                                            </template>
                                        </div>
                                        <template v-if="row.status !== 'matched'">
                                            <b-form-select
                                                v-model="decisionFor(row).action"
                                                :options="actionOptions(row)"
                                                size="sm"
                                                :state="decisionIsComplete(row) ? null : false"
                                                :disabled="applying"
                                                @change="onDecisionActionChanged(row)"
                                            ></b-form-select>

                                            <div
                                                v-if="decisionFor(row).action === 'match'"
                                                class="one-c-import-target"
                                            >
                                                <b-form-select
                                                    v-if="candidateOptions(row).length > 1"
                                                    v-model.number="decisionFor(row).employee_id"
                                                    :options="candidateOptions(row)"
                                                    size="sm"
                                                    :disabled="applying"
                                                    @change="onEmployeeChanged(row)"
                                                ></b-form-select>
                                                <b-form-input
                                                    v-model.number="decisionFor(row).employee_id"
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    size="sm"
                                                    placeholder="ID сотрудника Аргуса"
                                                    :state="validEmployeeId(decisionFor(row).employee_id) ? null : false"
                                                    :disabled="applying"
                                                    @input="onEmployeeChanged(row)"
                                                ></b-form-input>
                                            </div>
                                            <small
                                                v-if="decisionFor(row).action === 'create'"
                                                class="text-muted d-block mt-2"
                                            >
                                                Будет создан сотрудник без учётной записи Аргуса.
                                            </small>
                                            <router-link v-if="decisionFor(row).action === 'match' && validEmployeeId(decisionFor(row).employee_id)"
                                                :to="{name: 'employee-details', params: {id: decisionFor(row).employee_id}}" target="_blank" rel="noopener noreferrer" class="small d-block mt-2">
                                                Открыть выбранную карточку ↗
                                            </router-link>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-if="filteredReviewRows.length > rowsPerPage"
                        class="one-c-import-pagination"
                    >
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="filteredReviewRows.length"
                            :per-page="rowsPerPage"
                            size="sm"
                            class="mb-0"
                        ></b-pagination>
                        <span>
                            {{ reviewPageStart }}–{{ reviewPageEnd }}
                            из {{ filteredReviewRows.length }}
                        </span>
                    </div>
                </section>

                <section class="one-c-import-section one-c-import-apply">
                    <div class="one-c-import-section__heading">
                        <span class="one-c-import-step">4</span>
                        <div>
                            <h2>Применение импорта</h2>
                            <p>
                                Данные будут записаны только в базу текущей компании.
                                Отменить весь импорт одной кнопкой после применения нельзя.
                            </p>
                        </div>
                    </div>

                    <div class="one-c-import-readiness">
                        <div class="is-ready">
                            <i class="fas fa-check-circle" aria-hidden="true"></i>
                            Рабочий отдел Аргуса не изменяется
                        </div>
                        <div :class="decisionsReady ? 'is-ready' : 'is-blocked'">
                            <i
                                :class="decisionsReady ? 'fas fa-check-circle' : 'fas fa-times-circle'"
                                aria-hidden="true"
                            ></i>
                            Все спорные строки и формы найма разобраны
                        </div>
                        <div class="is-ready">
                            <i class="fas fa-check-circle" aria-hidden="true"></i>
                            Официальное подразделение хранится отдельно
                        </div>
                        <div class="is-ready">
                            <i class="fas fa-check-circle" aria-hidden="true"></i>
                            Статусы и договоры будут обработаны по результатам проверки
                        </div>
                    </div>

                    <b-form-checkbox
                        v-model="applyConfirmed"
                        :disabled="!baseApplyReady || applying"
                        class="one-c-import-confirm"
                    >
                        Я проверил сотрудников, договоры и решения по спорным строкам
                    </b-form-checkbox>

                    <div class="one-c-import-section__footer">
                        <span class="text-muted">
                            Перед записью сервер повторно проверит файл по SHA-256.
                        </span>
                        <b-button variant="danger" :disabled="!canApply" @click="applyImport">
                            <b-spinner v-if="applying" small class="mr-1"></b-spinner>
                            <i v-else class="fas fa-database mr-1" aria-hidden="true"></i>
                            Применить импорт
                        </b-button>
                    </div>
                </section>
            </template>
        </template>
    </div>
</template>

<script>
import api from '@/utils/api';
import {deferredGroups, deferRows} from './oneCImportDeferredReview';
import {createSingleFileFormData, uploadFileWithRetry} from '@/utils/fileUploadQueue';
import {formatPhone} from '@/utils/phone';
import {hiringFormLabel} from './employeeHiringForm';
import EmployeeMatchCandidates from './EmployeeMatchCandidates.vue';
import {planOneCBulkMatches} from './oneCImportBulkMatch';

const NORMALIZED_SPACES_RE = /\s+/g;
const IMPORT_SCOPE = 'current_employees';

export default {
    name: 'EmployeeOneCImport',
    components: {EmployeeMatchCandidates},
    data() {
        return {
            selectedFile: null,
            fileReading: false,
            preview: null,
            previewLoading: false,
            employeeReviewScheduled: false,
            mappingLoading: false,
            mappingSaving: false,
            applying: false,
            bulkMatchConfirming: false,
            applyResult: null,
            errorMessage: '',
            departments: [],
            savedMappings: [],
            departmentSelections: {},
            departmentQuery: '',
            decisions: {},
            rowQuery: '',
            rowFilter: 'disputed',
            currentPage: 1,
            rowsPerPage: 25,
            applyConfirmed: false,
            hiringFormChoices: [{value: '', text: 'По правилам импорта'}, {value: 'staff', text: 'Выбрать Штат вручную'}, {value: 'gph', text: 'Выбрать ГПХ вручную'}]
        };
    },
    computed: {
        employeeSelectionKey() {
            return JSON.stringify(this.reviewRows.map(row => {
                const decision = this.decisionFor(row);
                return [row.decision_key, decision.action, decision.employee_id];
            }));
        },
        isGphImport() { return this.preview && this.preview.metadata && this.preview.metadata.report_kind === 'gph'; },
        busy() {
            return this.fileReading || this.previewLoading || this.mappingSaving || this.applying;
        },
        previewSummary() {
            return this.preview && this.preview.summary || {};
        },
        scopeSummary() {
            return this.preview && this.preview.scope_summary || {};
        },
        resultSummary() {
            return this.applyResult && this.applyResult.summary || {};
        },
        sourceDepartments() {
            const metadata = this.preview && this.preview.metadata || {};
            const explicit = this.preview && this.preview.source_departments;
            const metadataDepartments = metadata.source_departments;
            const values = [];
            const append = value => {
                const name = String(value || '').trim();
                if (
                    name
                    && !values.some(item => this.normalizeText(item) === this.normalizeText(name))
                ) {
                    values.push(name);
                }
            };
            (Array.isArray(explicit) ? explicit : []).forEach(append);
            (Array.isArray(metadataDepartments) ? metadataDepartments : []).forEach(append);
            if (!values.length) {
                (this.preview && this.preview.rows || []).forEach(row => {
                    append(this.rowSourceDepartment(row));
                });
            }
            return values.sort((left, right) => left.localeCompare(right, 'ru'));
        },
        visibleSourceDepartments() {
            const query = this.normalizeText(this.departmentQuery);
            if (!query) {
                return this.sourceDepartments;
            }
            return this.sourceDepartments.filter(name => (
                this.normalizeText(name).indexOf(query) !== -1
            ));
        },
        mappedDepartmentCount() {
            return this.sourceDepartments.length - this.unmappedDepartments.length;
        },
        unmappedDepartments() {
            return this.sourceDepartments.filter(sourceName => (
                !this.departmentExists(this.departmentSelections[sourceName])
            ));
        },
        mappingChanges() {
            return this.sourceDepartments.reduce((result, sourceName) => {
                const saved = this.savedMappingForSource(sourceName);
                const savedActiveDepartmentId = (
                    saved
                    && saved.active !== false
                    && this.departmentExists(saved.department_id)
                )
                    ? Number(saved.department_id)
                    : null;
                const selectedDepartmentId = this.departmentExists(
                    this.departmentSelections[sourceName]
                )
                    ? Number(this.departmentSelections[sourceName])
                    : null;
                if (savedActiveDepartmentId === selectedDepartmentId) {
                    return result;
                }
                if (selectedDepartmentId) {
                    result.push({
                        source_department_name: sourceName,
                        department_id: selectedDepartmentId,
                        active: true
                    });
                } else if (saved && this.validEmployeeId(saved.department_id)) {
                    result.push({
                        source_department_name: sourceName,
                        department_id: Number(saved.department_id),
                        active: false
                    });
                }
                return result;
            }, []);
        },
        mappingsDirty() {
            return this.mappingChanges.length > 0;
        },
        canSaveMappings() {
            return (
                this.mappingsDirty
                && !this.mappingSaving
                && !this.previewLoading
            );
        },
        departmentOptions() {
            const options = [{value: null, text: 'Выберите отдел Аргуса'}];
            return options.concat(this.departments.map(department => ({
                value: this.departmentId(department),
                text: this.departmentName(department),
                disabled: !this.departmentId(department)
            })));
        },
        reviewRows() {
            return this.preview && Array.isArray(this.preview.rows)
                ? this.preview.rows
                : [];
        },
        bulkMatchPlan() {
            return planOneCBulkMatches(this.reviewRows, this.decisions);
        },
        deferPlan() { return deferredGroups(this.reviewRows, this.decisions); },
        bulkMatchKeys() {
            return new Set(this.bulkMatchPlan.items.map(item => item.decision_key));
        },
        disputedRows() {
            return this.reviewRows.filter(row => row.status !== 'matched'
                || row.activation_available || row.hiring_form_preview && (
                    row.hiring_form_preview.requires_choice || row.hiring_form_preview.manual_preserved));
        },
        unresolvedRowCount() {
            return this.disputedRows.filter(row => !this.decisionIsComplete(row)).length;
        },
        decisionsReady() {
            return this.unresolvedRowCount === 0;
        },
        rowFilterOptions() {
            return [
                {value: 'disputed', text: `Требуют решения (${this.disputedRows.length})`},
                {value: 'unresolved', text: `Не разобраны (${this.unresolvedRowCount})`},
                {value: 'bulk_match', text: `Для массового связывания (${this.bulkMatchPlan.row_count})`},
                {value: 'conflict', text: `Конфликты (${this.statusCount('conflict')})`},
                {value: 'unmatched', text: `Не найдены (${this.statusCount('unmatched')})`},
                {
                    value: 'duplicate_source',
                    text: `Дубли (${this.statusCount('duplicate_source')})`
                },
                {value: 'all', text: `Все строки (${this.reviewRows.length})`}
            ];
        },
        filteredReviewRows() {
            const query = this.normalizeText(this.rowQuery);
            return this.reviewRows.filter(row => {
                if (this.rowFilter === 'bulk_match' && !this.bulkMatchKeys.has(row.decision_key)) return false;
                if (this.rowFilter === 'disputed' && !this.disputedRows.includes(row)) {
                    return false;
                }
                if (this.rowFilter === 'unresolved' && this.decisionIsComplete(row)) {
                    return false;
                }
                if (
                    ['conflict', 'unmatched', 'duplicate_source'].indexOf(this.rowFilter) !== -1
                    && row.status !== this.rowFilter
                ) {
                    return false;
                }
                if (!query) {
                    return true;
                }
                const haystack = [
                    row.source_display_name,
                    this.rowPersonnelNumber(row),
                    this.rowPosition(row),
                    this.rowSourceDepartment(row),
                    row.row_number
                ].map(this.normalizeText).join(' ');
                return haystack.indexOf(query) !== -1;
            });
        },
        pagedReviewRows() {
            const start = (this.currentPage - 1) * this.rowsPerPage;
            return this.filteredReviewRows.slice(start, start + this.rowsPerPage);
        },
        reviewPageStart() {
            if (!this.filteredReviewRows.length) {
                return 0;
            }
            return (this.currentPage - 1) * this.rowsPerPage + 1;
        },
        reviewPageEnd() {
            return Math.min(
                this.currentPage * this.rowsPerPage,
                this.filteredReviewRows.length
            );
        },
        baseApplyReady() {
            return (
                Boolean(this.preview && this.selectedFile)
                && this.decisionsReady
            );
        },
        canApply() {
            return this.baseApplyReady && this.applyConfirmed && !this.busy;
        }
    },
    watch: {
        employeeSelectionKey() {
            this.scheduleEmployeeReview();
        },
        rowFilter() {
            this.currentPage = 1;
        },
        rowQuery() {
            this.currentPage = 1;
        }
    },
    async created() {
        await this.loadMappings();
    },
    beforeDestroy() {
        clearTimeout(this.employeeReviewTimer);
        this.employeeReviewDisposed = true;
    },
    methods: {
        needsEmployeeReview(row) {
            const decision = this.decisionFor(row);
            return decision.action === 'match' && this.validEmployeeId(decision.employee_id)
                && Number(decision.employee_id) !== Number(row.review_employee_id);
        },
        scheduleEmployeeReview() {
            clearTimeout(this.employeeReviewTimer);
            this.employeeReviewScheduled = false;
            if (this.employeeReviewDisposed || !this.reviewRows.some(this.needsEmployeeReview)) return;
            this.employeeReviewScheduled = true;
            this.employeeReviewTimer = setTimeout(() => {
                this.employeeReviewScheduled = false;
                this.previewSelectedFile({preserveDecisions: true, automatic: true});
            }, 450);
        },
        onEmployeeChanged(row) {
            const decision = this.decisionFor(row);
            decision.hiring_form = '';
            decision.accept_hiring_form_from_1c = false;
            decision.activate_employee = false;
            decision.confirmed_manual_termination_event_id = null;
            this.applyConfirmed = false;
        },
        hiringFormLabel,
        lifecyclePreviewText(row) {
            const labels = {active: 'Активен', reserve: 'Резерв', unverified: 'Требуется сверка'};
            const state = row.lifecycle_preview;
            if (!state.current) return 'Новая карточка: ' + (labels[state.proposed] || state.proposed);
            const target = row.activation_available && this.decisionFor(row).activate_employee
                ? 'active' : state.proposed;
            return target === state.current
                ? 'Статус без изменений: ' + (labels[state.current] || state.current)
                : 'Статус: ' + (labels[state.current] || state.current) + ' → ' + (labels[target] || target);
        },
        errorText(error, fallback) {
            const data = error && error.response && error.response.data;
            if (data && typeof data.msg === 'string') {
                return data.msg;
            }
            if (typeof data === 'string') {
                return data;
            }
            if (error && !error.response && error.message === 'Network Error') {
                return 'Не удалось получить ответ сервера на проверку. Выбранные решения сохранены на этой странице. Причиной может быть разрыв соединения или длительная обработка. Повторите проверку.';
            }
            if (error && error.message) {
                return error.message;
            }
            return fallback;
        },
        normalizeText(value) {
            return String(value || '')
                .trim()
                .toLocaleLowerCase('ru')
                .replace(NORMALIZED_SPACES_RE, ' ');
        },
        validEmployeeId(value) {
            return Number.isInteger(Number(value)) && Number(value) > 0;
        },
        departmentExists(value) {
            const id = Number(value);
            return this.validEmployeeId(id) && this.departments.some(department => (
                this.departmentId(department) === id
            ));
        },
        departmentId(department) {
            const value = department && (
                department.department_id
                || department.id
                || department.value
            );
            return this.validEmployeeId(value) ? Number(value) : null;
        },
        departmentName(department) {
            return String(
                department && (
                    department.department_name
                    || department.name
                    || department.text
                )
                || `Отдел #${this.departmentId(department) || '?'}`
            );
        },
        mappingControlId(sourceName) {
            let hash = 0;
            String(sourceName || '').split('').forEach(character => {
                hash = ((hash << 5) - hash) + character.charCodeAt(0);
                hash |= 0;
            });
            return `one-c-department-${Math.abs(hash)}`;
        },
        normalizeMappingResponse(data) {
            if (Array.isArray(data)) {
                return {mappings: data, departments: []};
            }
            const payload = data || {};
            return {
                mappings: Array.isArray(payload.mappings)
                    ? payload.mappings
                    : (
                        Array.isArray(payload.department_mappings)
                            ? payload.department_mappings
                            : []
                    ),
                departments: Array.isArray(payload.departments) ? payload.departments : []
            };
        },
        mergeDepartments(items) {
            const byId = new Map();
            this.departments.concat(Array.isArray(items) ? items : []).forEach(department => {
                const id = this.departmentId(department);
                if (id && !byId.has(id)) {
                    byId.set(id, department);
                }
            });
            this.departments = Array.from(byId.values()).sort((left, right) => (
                this.departmentName(left).localeCompare(this.departmentName(right), 'ru')
            ));
        },
        savedMappingForSource(sourceName) {
            const sourceKey = this.normalizeText(sourceName);
            return (this.savedMappings || []).find(item => (
                this.normalizeText(
                    item && (
                        item.normalized_source_department
                        || item.source_department_name
                    )
                ) === sourceKey
            )) || null;
        },
        async loadMappings() {
            this.mappingLoading = true;
            try {
                const response = await api.get(
                    '/api/employees/imports/1c-zup/department-mappings',
                    {ignoreModal: true}
                );
                const normalized = this.normalizeMappingResponse(response.data);
                this.savedMappings = normalized.mappings;
                this.mergeDepartments(normalized.departments);
                this.initializeDepartmentSelections();
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось загрузить сопоставления подразделений.'
                );
            } finally {
                this.mappingLoading = false;
            }
        },
        initializeDepartmentSelections() {
            const selections = {};
            const activeMappings = (this.savedMappings || []).filter(mapping => (
                mapping && mapping.active !== false
            ));
            this.sourceDepartments.forEach(sourceName => {
                const sourceKey = this.normalizeText(sourceName);
                const mapping = activeMappings.find(item => (
                    this.normalizeText(
                        item.normalized_source_department
                        || item.source_department_name
                    ) === sourceKey
                ));
                selections[sourceName] = (
                    mapping
                    && this.departmentExists(mapping.department_id)
                )
                    ? Number(mapping.department_id)
                    : null;
            });
            this.departmentSelections = selections;
        },
        chooseFile() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },
        async onFileSelected(event) {
            const file = event && event.target && event.target.files
                ? event.target.files[0]
                : null;
            if (event && event.target) {
                event.target.value = '';
            }
            if (!file || this.busy) {
                return;
            }
            if (!/\.xlsx$/i.test(file.name || '')) {
                this.errorMessage = 'Выберите файл Excel в формате XLSX.';
                return;
            }
            if (file.size > 30 * 1024 * 1024) {
                this.errorMessage = 'Размер XLSX-файла не должен превышать 30 МБ.';
                return;
            }
            this.fileReading = true;
            try {
                // Keep the reviewed bytes in memory. Re-saving the original XLSX
                // must not invalidate later XHR uploads or change the apply file.
                const bytes = await file.arrayBuffer();
                if (this.employeeReviewDisposed) return;
                this.selectedFile = new File([bytes], file.name, {type: file.type, lastModified: file.lastModified});
            } catch (error) {
                this.errorMessage = 'Не удалось прочитать XLSX. Возможно, файл был изменён или перемещён. Выберите его заново.';
                return;
            } finally {
                this.fileReading = false;
            }
            this.preview = null;
            this.applyResult = null;
            this.decisions = {};
            this.departmentSelections = {};
            this.applyConfirmed = false;
            this.errorMessage = '';
            await this.previewSelectedFile();
        },
        async previewSelectedFile(options) {
            if (!this.selectedFile || this.previewLoading) {
                return;
            }
            const preserveDecisions = Boolean(this.preview || options && options.preserveDecisions);
            const automatic = Boolean(options && options.automatic);
            const selectionKey = this.employeeSelectionKey;
            this.previewLoading = true;
            this.errorMessage = '';
            try {
                const fields = {
                    import_scope: IMPORT_SCOPE,
                    decisions: JSON.stringify(this.previewDecisionsPayload())
                };
                const response = await uploadFileWithRetry(file => api.post(
                    '/api/employees/imports/1c-zup/preview',
                    createSingleFileFormData('file', file, fields),
                    {
                        headers: {'Content-Type': 'multipart/form-data'},
                        ignoreModal: true
                    }
                ), this.selectedFile, {
                    shouldRetry: error => {
                        if (this.employeeReviewDisposed || selectionKey !== this.employeeSelectionKey) return false;
                        if (!error.response) return error.message === 'Network Error' || error.code === 'ECONNABORTED';
                        return [408, 429, 502, 503, 504].includes(Number(error.response.status));
                    }
                });
                if (this.employeeReviewDisposed || selectionKey !== this.employeeSelectionKey) return;
                const oldDecisions = preserveDecisions ? this.decisions : {};
                this.preview = response.data;
                this.mergeDepartments(response.data && response.data.departments);
                if (!automatic) this.initializeDepartmentSelections();
                this.initializeDecisions(oldDecisions);
                if (!automatic) this.currentPage = 1;
            } catch (error) {
                if (this.employeeReviewDisposed || selectionKey !== this.employeeSelectionKey) return;
                if (!preserveDecisions) this.preview = null;
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось проверить выгрузку 1С.'
                );
            } finally {
                this.previewLoading = false;
                if (selectionKey !== this.employeeSelectionKey) this.scheduleEmployeeReview();
            }
        },
        initializeDecisions(previous) {
            const decisions = {};
            this.reviewRows.forEach(row => {
                const oldDecision = previous && previous[row.decision_key];
                const next = {
                    decision_key: row.decision_key,
                    source_record_key: row.source_record_key || null,
                    action: null,
                    employee_id: null,
                    hiring_form: '',
                    accept_hiring_form_from_1c: false,
                    activate_employee: false,
                    reject_candidate_ids: [],
                    confirmed_manual_termination_event_id: null
                };
                const terminationEventId = this.manualTerminationEventId(row);
                const oldTerminationEventId = Number(
                    oldDecision
                    && oldDecision.confirmed_manual_termination_event_id
                ) || null;
                const staleConfirmationIsCurrent = !terminationEventId
                    || !oldDecision
                    || oldDecision.action !== 'match'
                    || oldTerminationEventId === terminationEventId;
                if (
                    oldDecision
                    && this.actionAllowed(row, oldDecision.action)
                ) {
                    next.action = oldDecision.action;
                    next.employee_id = oldDecision.employee_id || null;
                    next.confirmed_manual_termination_event_id =
                        next.action === 'match' && oldTerminationEventId === terminationEventId
                            ? oldTerminationEventId
                            : null;
                }
                if (oldDecision && staleConfirmationIsCurrent) {
                    next.reject_candidate_ids = (oldDecision.reject_candidate_ids || []).filter(id => (row.match_candidates || []).some(candidate => candidate.employee_id === id));
                    next.hiring_form = oldDecision.hiring_form || '';
                    next.accept_hiring_form_from_1c = oldDecision.accept_hiring_form_from_1c === true;
                    next.activate_employee = oldDecision.activate_employee === true;
                    next.confirmed_manual_termination_event_id = oldTerminationEventId === terminationEventId ? oldTerminationEventId : null;
                }
                decisions[row.decision_key] = next;
            });
            this.decisions = decisions;
            this.applyConfirmed = false;
        },
        onMappingChanged() {
            this.applyConfirmed = false;
        },
        async saveMappings() {
            if (!this.canSaveMappings) {
                return;
            }
            this.mappingSaving = true;
            this.errorMessage = '';
            try {
                const mappings = this.mappingChanges.slice();
                const response = await api.put(
                    '/api/employees/imports/1c-zup/department-mappings',
                    {mappings},
                    {ignoreModal: true}
                );
                const normalized = this.normalizeMappingResponse(response.data);
                this.savedMappings = normalized.mappings.length
                    ? normalized.mappings
                    : mappings;
                this.mergeDepartments(normalized.departments);
                await this.previewSelectedFile({preserveDecisions: true});
                this.$bvToast.toast(
                    'Справочные сопоставления сохранены. Рабочие отделы сотрудников не изменены.',
                    {
                        title: 'Сопоставления сохранены',
                        variant: 'success',
                        solid: true
                    }
                );
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось сохранить сопоставления подразделений.'
                );
            } finally {
                this.mappingSaving = false;
            }
        },
        decisionFor(row) {
            return this.decisions[row.decision_key] || {
                action: null,
                employee_id: null,
                confirmed_manual_termination_event_id: null
            };
        },
        manualTerminationEventId(row) {
            const directId = Number(
                row && row.manual_termination_event_id
            ) || null;
            if (directId) {
                return directId;
            }
            const staleIssue = (Array.isArray(row && row.issues)
                ? row.issues
                : []
            ).find(issue => (
                issue
                && issue.code === 'ONE_C_STALE_AFTER_MANUAL_TERMINATION'
            ));
            return Number(
                staleIssue && staleIssue.termination_event_id
            ) || null;
        },
        actionAllowed(row, action) {
            return this.actionOptions(row).some(option => option.value === action);
        },
        actionOptions(row) {
            const required = [{value: null, text: 'Выберите действие'}];
            if (row.status === 'duplicate_source') {
                return required.concat([
                    {value: 'ignore', text: 'Игнорировать дубликат'}
                ]);
            }
            if (row.status === 'conflict') {
                const matchText = this.manualTerminationEventId(row)
                    ? 'Подтвердить возобновление и связать'
                    : 'Связать с сотрудником Аргуса';
                return required.concat([
                    {value: 'match', text: matchText},
                    ...(row.can_defer ? [{value: 'defer', text: 'Импортировать и отправить на сверку'}] : []),
                    ...(row.can_create_after_review ? [{value: 'create', text: 'Никто из списка — создать нового и запомнить'}] : []),
                    {value: 'ignore', text: 'Не импортировать строку'}
                ]);
            }
            if (row.status === 'unmatched') {
                return required.concat([
                    {value: 'create', text: 'Создать нового сотрудника'},
                    {value: 'match', text: 'Связать с существующим'},
                    {value: 'ignore', text: 'Не импортировать строку'}
                ]);
            }
            return [{value: 'match', text: 'Обновить найденного сотрудника'}];
        },
        onDecisionActionChanged(row) {
            const decision = this.decisionFor(row);
            if (decision.action === 'defer') {
                const related = this.reviewRows.filter(item => item.source_subject === row.source_subject && item.status !== 'duplicate_source');
                if (related.every(item => item.can_defer && (!this.decisionFor(item).action || this.decisionFor(item).action === 'defer'))) {
                    this.decisions = deferRows(related, this.decisions);
                } else {
                    decision.action = null;
                    this.errorMessage = 'По другому договору этого человека уже выбрано решение. Согласуйте решения по всем договорам.';
                }
                this.applyConfirmed = false;
                this.scheduleEmployeeReview();
                return;
            }
            if (decision.action !== 'match') {
                decision.employee_id = null;
                decision.confirmed_manual_termination_event_id = null;
            } else if (
                row.matched_employee
                && this.validEmployeeId(row.matched_employee.employee_id)
            ) {
                decision.employee_id = Number(row.matched_employee.employee_id);
            } else {
                const candidates = this.uniqueCandidates(row);
                if (candidates.length === 1) {
                    decision.employee_id = Number(candidates[0].employee_id);
                }
            }
            if (decision.action === 'match') {
                decision.confirmed_manual_termination_event_id =
                    this.manualTerminationEventId(row);
            }
            this.applyConfirmed = false;
            this.onEmployeeChanged(row);
        },
        async bulkMatchEmployees() {
            if (this.busy || this.bulkMatchConfirming || !this.bulkMatchPlan.row_count) return;
            const plan = this.bulkMatchPlan;
            const preview = this.preview;
            const file = this.selectedFile;
            const decisionsSnapshot = JSON.stringify(this.decisions);
            this.bulkMatchConfirming = true;
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Выбрать найденные карточки для ${plan.people_count} человек по ${plan.row_count} строкам всего файла? `
                    + 'У каждого один кандидат: фамилия совпадает, имя и отчество совпадают полностью или по инициалам. '
                    + 'Дата рождения может отсутствовать; разные даты и опечатки исключены. '
                    + 'Выбранные ранее решения сохранятся. Данные запишутся только после «Применить импорт».',
                    {title: 'Массовое связывание', okTitle: `Выбрать карточки (${plan.people_count})`,
                        cancelTitle: 'Отмена', okVariant: 'primary', centered: true}
                );
                if (!confirmed || this.employeeReviewDisposed) return;
                if (this.busy || this.preview !== preview || this.selectedFile !== file
                    || JSON.stringify(this.decisions) !== decisionsSnapshot) {
                    this.errorMessage = 'Сверка изменилась во время подтверждения. Повторите массовое связывание по обновлённым данным.';
                    return;
                }
                const next = Object.assign({}, this.decisions);
                plan.items.forEach(item => {
                    next[item.decision_key] = Object.assign({}, next[item.decision_key], {
                        action: 'match', employee_id: item.employee_id, hiring_form: '',
                        accept_hiring_form_from_1c: false, activate_employee: false,
                        confirmed_manual_termination_event_id: null
                    });
                });
                // One reactive update produces one debounced preview for the whole batch.
                this.decisions = next;
                this.applyConfirmed = false;
                this.errorMessage = '';
                this.rowFilter = 'unresolved';
                this.currentPage = 1;
            } finally {
                this.bulkMatchConfirming = false;
            }
        },
        async bulkDefer() {
            if (this.busy || this.bulkMatchConfirming || !this.deferPlan.length) return;
            const groups = this.deferPlan;
            const preview = this.preview;
            const file = this.selectedFile;
            const snapshot = JSON.stringify(this.decisions);
            this.bulkMatchConfirming = true;
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Отправить ${groups.length} сотрудников на сверку после импорта? Будут сохранены отдельные карточки со всеми договорами и кандидатами. Текущие карточки Аргуса не объединяются.`,
                    {title: 'Отложенная сверка', okTitle: 'Подготовить решения', cancelTitle: 'Отмена', centered: true});
                if (!confirmed || this.employeeReviewDisposed || this.busy || this.preview !== preview
                    || this.selectedFile !== file || JSON.stringify(this.decisions) !== snapshot) return;
                this.decisions = deferRows(groups.reduce((rows, group) => rows.concat(group), []), this.decisions);
                this.applyConfirmed = false;
                this.scheduleEmployeeReview();
            } finally { this.bulkMatchConfirming = false; }
        },
        async bulkResolve(status, action) {
            const rows = this.reviewRows.filter(row => row.status === status);
            if (!rows.length) {
                return;
            }
            if (action === 'create') {
                const employeeCount = Number(
                    this.previewSummary.employees_unmatched
                ) || rows.length;
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Назначить создание карточек для ${employeeCount} человек `
                    + `по ${rows.length} строкам? Повторные трудовые периоды `
                    + `одного сотрудника будут объединены в одной карточке.`,
                    {
                        title: 'Массовое решение',
                        okTitle: 'Назначить создание',
                        cancelTitle: 'Отмена',
                        okVariant: 'primary',
                        centered: true
                    }
                );
                if (!confirmed) {
                    return;
                }
            }
            rows.forEach(row => {
                const decision = this.decisionFor(row);
                decision.action = action;
                decision.employee_id = null;
                decision.confirmed_manual_termination_event_id =
                    action === 'match'
                        ? this.manualTerminationEventId(row)
                        : null;
            });
            this.applyConfirmed = false;
        },
        resetManualDecisions() {
            this.initializeDecisions({});
        },
        decisionIsComplete(row) {
            const choice = this.decisionFor(row);
            if (choice.action === 'defer') return row.can_defer === true;
            if (choice.action === 'ignore') return true;
            if (choice.action === 'match' && Number(choice.employee_id) !== row.review_employee_id) return false;
            if (choice.activate_employee && row.manual_termination_event_id && Number(choice.confirmed_manual_termination_event_id) !== Number(row.manual_termination_event_id)) return false;
            if (row.hiring_form_preview && row.hiring_form_preview.requires_choice && !choice.hiring_form) return false;
            if (row.status === 'matched') {
                return true;
            }
            const decision = this.decisionFor(row);
            if (!decision.action || !this.actionAllowed(row, decision.action)) {
                return false;
            }
            if (decision.action === 'match') {
                const terminationEventId = this.manualTerminationEventId(row);
                return this.validEmployeeId(decision.employee_id)
                    && (
                        !terminationEventId
                        || Number(
                            decision.confirmed_manual_termination_event_id
                        ) === terminationEventId
                    );
            }
            return true;
        },
        previewDecisionsPayload() {
            // Preview uses only the selected person and create/ignore actions to
            // recalculate proposals. Audit tokens and confirmations belong to apply.
            return this.reviewRows.reduce((result, row) => {
                const decision = this.decisionFor(row);
                if (!['match', 'create', 'ignore', 'defer'].includes(decision.action)) return result;
                result[row.decision_key] = {action: decision.action};
                if (decision.action === 'match') {
                    result[row.decision_key].employee_id = Number(decision.employee_id) || null;
                }
                return result;
            }, {});
        },
        decisionsPayload() {
            return this.reviewRows.reduce((result, row) => {
                const decision = this.decisionFor(row);
                if (decision.action === 'defer') {
                    result[row.decision_key] = {action: 'defer', match_review_token: row.match_review_token};
                    return result;
                }
                const rejectedIds = (decision.reject_candidate_ids || []).filter(id => decision.action !== 'match' || Number(id) !== Number(decision.employee_id));
                const terminationId = decision.action === 'match' || decision.activate_employee
                    ? Number(decision.confirmed_manual_termination_event_id) || null : null;
                result[row.decision_key] = {
                    ...(rejectedIds.length ? {reject_candidate_ids: rejectedIds} : {}),
                    ...(decision.action === 'create' && row.can_create_after_review === true ? {reject_all_candidates: true} : {}),
                    match_review_token: row.match_review_token,
                    ...(decision.hiring_form ? {hiring_form: decision.hiring_form} : {}),
                    ...(decision.accept_hiring_form_from_1c === true ? {accept_hiring_form_from_1c: true} : {}),
                    ...(decision.activate_employee === true ? {activate_employee: true} : {}),
                    action: decision.action,
                    ...(decision.action === 'match' ? {employee_id: Number(decision.employee_id)} : {}),
                    ...(terminationId ? {confirmed_manual_termination_event_id: terminationId} : {})
                };
                return result;
            }, {});
        },
        uniqueCandidates(row) {
            const byId = new Map();
            const append = candidate => {
                const id = candidate && Number(candidate.employee_id);
                if (id && !byId.has(id)) {
                    byId.set(id, candidate);
                }
            };
            append(row.matched_employee);
            (Array.isArray(row.candidates) ? row.candidates : []).forEach(append);
            return Array.from(byId.values());
        },
        candidateOptions(row) {
            const options = [{value: null, text: 'Выберите найденного сотрудника'}];
            return options.concat(this.uniqueCandidates(row).map(candidate => ({
                value: Number(candidate.employee_id),
                text: this.candidateLabel(candidate)
            })));
        },
        candidateLabel(candidate) {
            const id = candidate && candidate.employee_id;
            const name = candidate && (
                candidate.display_name
                || candidate.full_name
                || [
                    candidate.last_name,
                    candidate.first_name,
                    candidate.middle_name
                ].filter(Boolean).join(' ')
            );
            const birthDate = candidate && candidate.birth_date;
            return [
                id ? `#${id}` : null,
                name || 'Сотрудник без имени',
                birthDate ? `д.р. ${this.formatDate(birthDate)}` : null
            ].filter(Boolean).join(' · ');
        },
        statusCount(status) {
            return this.reviewRows.filter(row => row.status === status).length;
        },
        statusLabel(status) {
            const labels = {
                matched: 'Надёжное совпадение',
                conflict: 'Нужна проверка',
                unmatched: 'Не найден в Аргусе',
                duplicate_source: 'Дубликат в файле'
            };
            return labels[status] || status || 'Неизвестно';
        },
        statusVariant(status) {
            const variants = {
                matched: 'success',
                conflict: 'danger',
                unmatched: 'warning',
                duplicate_source: 'secondary'
            };
            return variants[status] || 'secondary';
        },
        rowCanonical(row) {
            return row && (
                row.canonical
                || row.source && row.source.canonical
                || row.source_fields && row.source_fields.canonical
            ) || {};
        },
        rowEmployment(row) {
            return row && (
                row.employment
                || row.source && row.source.employment
                || row.source_fields && row.source_fields.employment
            ) || {};
        },
        rowPersonnelNumber(row) {
            const employment = this.rowEmployment(row);
            return employment.personnel_number || row && row.personnel_number || null;
        },
        rowPosition(row) {
            const employment = this.rowEmployment(row);
            return (
                employment.official_position
                || employment.position
                || row && row.official_position
                || null
            );
        },
        rowSourceDepartment(row) {
            const employment = this.rowEmployment(row);
            return (
                employment.source_department
                || employment.department
                || row && row.source_department
                || null
            );
        },
        rowBirthDate(row) {
            const canonical = this.rowCanonical(row);
            return canonical.birth_date || row && row.birth_date || null;
        },
        rowEmploymentPeriod(row) {
            const employment = this.rowEmployment(row);
            const hiredOn = employment.hired_on;
            const dismissedOn = employment.dismissed_on;
            if (employment.hiring_form === 'gph') {
                const state = {active: 'Действует', expired: 'Завершён', future: 'Будущий'}[employment.contract_status] || '';
                const label = employment.document_number
                    ? `ГПХ (документ 1С № ${employment.document_number} от ${this.formatDate(employment.document_date)})`
                    : `ГПХ № ${employment.contract_number || '—'}`;
                return `${label}: ${this.formatDate(hiredOn)} — ${this.formatDate(dismissedOn)}. ${state}`;
            }
            if (!hiredOn && !dismissedOn && typeof employment.is_current === 'undefined') {
                return null;
            }
            const state = employment.is_current === false ? 'Уволен' : 'Действующий';
            const dates = [
                hiredOn ? `приём ${this.formatDate(hiredOn)}` : null,
                dismissedOn ? `увольнение ${this.formatDate(dismissedOn)}` : null
            ].filter(Boolean).join(', ');
            return dates ? `${state}: ${dates}` : state;
        },
        rowPhone(row) {
            const canonical = this.rowCanonical(row);
            const methods = Array.isArray(canonical.contact_methods)
                ? canonical.contact_methods
                : [];
            const phone = methods.find(item => item && item.contact_type === 'phone');
            return phone && phone.value ? formatPhone(phone.value) : null;
        },
        rowMessages(row) {
            const messages = [];
            const append = value => {
                const message = typeof value === 'string'
                    ? value
                    : value && (value.message || value.text || value.code);
                if (message && messages.indexOf(message) === -1) {
                    messages.push(message);
                }
            };
            (Array.isArray(row.issues) ? row.issues : []).forEach(append);
            (Array.isArray(row.warnings) ? row.warnings : []).forEach(append);
            return messages;
        },
        formatDate(value) {
            const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
            return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value || '');
        },
        formatFileSize(bytes) {
            const size = Number(bytes) || 0;
            if (size < 1024) {
                return `${size} Б`;
            }
            if (size < 1024 * 1024) {
                return `${Math.round(size / 1024)} КБ`;
            }
            return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
        },
        shortHash(value) {
            const hash = String(value || '');
            return hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : hash || '—';
        },
        async applyImport() {
            if (!this.canApply) {
                return;
            }
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Импорт создаст и обновит карточки сотрудников в текущей компании. Спорные записи с действием «На сверку» будут сохранены отдельными карточками без объединения. Продолжить?',
                {
                    title: 'Подтверждение импорта 1С',
                    okTitle: 'Применить импорт',
                    cancelTitle: 'Вернуться к проверке',
                    okVariant: 'danger',
                    cancelVariant: 'outline-secondary',
                    centered: true
                }
            );
            if (!confirmed || !this.canApply) {
                return;
            }

            this.applying = true;
            this.errorMessage = '';
            try {
                const formData = createSingleFileFormData('file', this.selectedFile, {
                    apply: 'true',
                    import_scope: IMPORT_SCOPE,
                    expected_file_sha256: this.preview.file_sha256 || '',
                    expected_records_sha256: this.preview.records_sha256 || '',
                    expected_reference_date: this.preview.reference_date || '',
                    decisions: JSON.stringify(this.decisionsPayload())
                });
                const response = await api.post(
                    '/api/employees/imports/1c-zup/apply',
                    formData,
                    {
                        headers: {'Content-Type': 'multipart/form-data'},
                        ignoreModal: true
                    }
                );
                const result = response && response.data || null;
                if (!result || result.status !== 'applied' || !result.summary) {
                    throw new Error('Сервер не вернул результат применения импорта 1С.');
                }
                this.applyResult = result;
                this.$bvToast.toast(
                    'Кадровые данные из 1С перенесены в текущую компанию.',
                    {
                        title: 'Импорт завершён',
                        variant: 'success',
                        solid: true
                    }
                );
                window.scrollTo({top: 0, behavior: 'smooth'});
            } catch (error) {
                this.applyConfirmed = false;
                this.errorMessage = !error.response || Number(error.response.status) >= 500
                    ? 'Ответ о результате импорта не получен. Сервер мог продолжить обработку и сохранить данные. Перед повторным применением проверьте результат в карточках сотрудников. Выбранные решения сохранены на этой странице.'
                    : this.errorText(
                    error,
                    'Не удалось применить импорт 1С.'
                );
            } finally {
                this.applying = false;
            }
        },
        startAnotherImport() {
            this.selectedFile = null;
            this.preview = null;
            this.applyResult = null;
            this.decisions = {};
            this.departmentSelections = {};
            this.departmentQuery = '';
            this.rowQuery = '';
            this.rowFilter = 'disputed';
            this.currentPage = 1;
            this.applyConfirmed = false;
            this.errorMessage = '';
            this.chooseFile();
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.openBoard();
        },
        openBoard() {
            this.$router.push('/employees/workforce');
        }
    }
};
</script>

<style scoped>
.one-c-import-page {
    max-width: 1440px;
    margin: 0 auto;
    padding: 22px;
    color: #213547;
}

.one-c-import-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 18px;
}

.one-c-import-header h1 {
    margin: 4px 0;
    font-size: 1.65rem;
    font-weight: 700;
}

.one-c-import-header p,
.one-c-import-section__heading p {
    margin: 0;
    color: #6b7785;
}

.one-c-import-back {
    padding: 0;
    border: 0;
    background: transparent;
    color: #4267a5;
    font-size: 0.88rem;
}

.one-c-import-back i {
    margin-right: 6px;
}

.one-c-import-header__tenant {
    padding: 9px 12px;
    border: 1px solid #dfe5eb;
    color: #415466;
    font-size: 0.82rem;
}

.one-c-import-scope {
    margin-bottom: 18px;
}

.one-c-import-section {
    margin-bottom: 18px;
    padding: 20px;
    border: 1px solid #dfe5eb;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(33, 53, 71, 0.05);
}

.one-c-import-section__heading {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    margin-bottom: 18px;
}

.one-c-import-section__heading h2,
.one-c-import-result h2 {
    margin: 0 0 3px;
    font-size: 1.1rem;
    font-weight: 700;
}

.one-c-import-step {
    display: inline-flex;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #e9f1ff;
    color: #2368c4;
    font-weight: 700;
}

.one-c-import-step--done {
    background: #dff3e7;
    color: #24844e;
}

.one-c-import-file {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border: 1px dashed #aebdca;
    border-radius: 9px;
    background: #f9fbfd;
}

.one-c-import-file__info {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    min-width: 0;
    gap: 12px;
}

.one-c-import-file__info > i {
    color: #278250;
    font-size: 1.7rem;
}

.one-c-import-file__info div {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.one-c-import-file__info strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.one-c-import-file__info span {
    color: #71808e;
    font-size: 0.8rem;
}

.one-c-import-summary {
    display: grid;
    grid-template-columns: repeat(5, minmax(130px, 1fr));
    gap: 10px;
}

.one-c-import-summary article {
    display: flex;
    min-height: 75px;
    flex-direction: column;
    justify-content: center;
    padding: 11px 13px;
    border: 1px solid #e0e6eb;
    border-radius: 8px;
    background: #f8fafc;
}

.one-c-import-summary span {
    color: #6f7d89;
    font-size: 0.76rem;
}

.one-c-import-summary strong {
    margin-top: 3px;
    font-size: 1.45rem;
}

.one-c-import-summary__success strong {
    color: #278250;
}

.one-c-import-summary__danger strong {
    color: #c23a47;
}

.one-c-import-summary__warning strong {
    color: #b17210;
}

.one-c-import-mapping-toolbar,
.one-c-import-review-toolbar,
.one-c-import-section__footer,
.one-c-import-pagination,
.one-c-import-result__actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.one-c-import-mapping-toolbar {
    justify-content: space-between;
    margin-bottom: 12px;
}

.one-c-import-mapping-toolbar .input-group {
    max-width: 410px;
}

.one-c-import-progress-label {
    color: #617181;
    font-size: 0.84rem;
    white-space: nowrap;
}

.one-c-import-mapping-list {
    max-height: 430px;
    overflow: auto;
    border: 1px solid #e1e6eb;
    border-radius: 8px;
}

.one-c-import-mapping-row {
    display: grid;
    grid-template-columns: minmax(230px, 1fr) 22px minmax(250px, 1fr);
    align-items: center;
    gap: 12px;
    padding: 11px 13px;
    border-bottom: 1px solid #edf0f3;
}

.one-c-import-mapping-row:last-child {
    border-bottom: 0;
}

.one-c-import-mapping-row > div:first-child {
    display: flex;
    flex-direction: column;
}

.one-c-import-mapping-row span,
.one-c-import-mapping-row label {
    margin: 0 0 2px;
    color: #758390;
    font-size: 0.73rem;
}

.one-c-import-mapping-row > i {
    color: #98a5b0;
}

.one-c-import-section__footer {
    justify-content: space-between;
    margin-top: 14px;
}

.one-c-import-review-toolbar {
    margin-bottom: 12px;
}

.one-c-import-review-search {
    max-width: 410px;
}

.one-c-import-review-toolbar > .custom-select {
    width: auto;
    min-width: 220px;
}

.one-c-import-review-toolbar .one-c-import-progress-label {
    margin-left: auto;
}

.one-c-import-table-wrap {
    overflow-x: auto;
    border: 1px solid #dfe5ea;
    border-radius: 8px;
}

.one-c-import-table {
    min-width: 920px;
    margin: 0;
}

.one-c-import-table th {
    padding: 10px;
    border-top: 0;
    background: #f1f4f7;
    color: #526474;
    font-size: 0.76rem;
    text-transform: uppercase;
    vertical-align: middle;
}

.one-c-import-table td {
    padding: 11px 10px;
    vertical-align: top;
}

.one-c-import-table th:first-child,
.one-c-import-table td:first-child {
    width: 31%;
}

.one-c-import-table th:nth-child(2),
.one-c-import-table td:nth-child(2) {
    width: 35%;
}

.one-c-import-person {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.one-c-import-person span {
    color: #647584;
    font-size: 0.78rem;
}

.one-c-import-match {
    display: flex;
    flex-direction: column;
    margin-top: 8px;
    color: #536574;
    font-size: 0.78rem;
}

.one-c-import-issues {
    margin: 8px 0 0;
    padding-left: 17px;
    color: #875c1c;
    font-size: 0.78rem;
}

.one-c-import-auto-decision {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #278250;
    font-size: 0.84rem;
    font-weight: 600;
}

.one-c-import-target {
    display: grid;
    grid-template-columns: minmax(190px, 1fr) minmax(150px, 0.55fr);
    gap: 7px;
    margin-top: 7px;
}

.one-c-import-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    padding: 28px;
    border: 1px dashed #cbd5de;
    border-radius: 8px;
    color: #4d7b61;
}

.one-c-import-empty i {
    font-size: 1.6rem;
}

.one-c-import-pagination {
    justify-content: space-between;
    margin-top: 12px;
    color: #71808d;
    font-size: 0.8rem;
}

.one-c-import-readiness {
    display: grid;
    grid-template-columns: repeat(3, minmax(180px, 1fr));
    gap: 9px;
    margin-bottom: 15px;
}

.one-c-import-readiness > div {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 7px;
    font-size: 0.82rem;
}

.one-c-import-readiness .is-ready {
    background: #e7f5ec;
    color: #24784a;
}

.one-c-import-readiness .is-blocked {
    background: #fff0f0;
    color: #a73b47;
}

.one-c-import-confirm {
    padding: 12px;
    border: 1px solid #e0e6eb;
    border-radius: 7px;
    background: #f8fafc;
}

.one-c-import-result {
    display: flex;
    gap: 18px;
    padding: 24px;
    border: 1px solid #c8e4d3;
    border-radius: 12px;
    background: #f5fbf7;
}

.one-c-import-result__icon {
    display: flex;
    flex: 0 0 45px;
    align-items: center;
    justify-content: center;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: #2f915b;
    color: #fff;
    font-size: 1.2rem;
}

.one-c-import-result > div:last-child {
    flex: 1 1 auto;
    min-width: 0;
}

.one-c-import-result__actions {
    margin-top: 17px;
}

@media (max-width: 900px) {
    .one-c-import-summary {
        grid-template-columns: repeat(2, minmax(130px, 1fr));
    }

    .one-c-import-readiness {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 700px) {
    .one-c-import-page {
        padding: 12px;
    }

    .one-c-import-header {
        flex-direction: column;
        gap: 10px;
    }

    .one-c-import-header h1 {
        font-size: 1.35rem;
    }

    .one-c-import-file,
    .one-c-import-mapping-toolbar,
    .one-c-import-review-toolbar,
    .one-c-import-section__footer,
    .one-c-import-result__actions {
        align-items: stretch;
        flex-direction: column;
    }

    .one-c-import-file__info {
        width: 100%;
    }

    .one-c-import-mapping-toolbar .input-group,
    .one-c-import-review-search,
    .one-c-import-review-toolbar > .custom-select {
        width: 100%;
        max-width: none;
    }

    .one-c-import-review-toolbar .one-c-import-progress-label {
        margin-left: 0;
    }

    .one-c-import-mapping-row {
        grid-template-columns: 1fr;
    }

    .one-c-import-mapping-row > i {
        display: none;
    }

    .one-c-import-summary {
        grid-template-columns: 1fr 1fr;
    }

    .one-c-import-target {
        grid-template-columns: 1fr;
    }

    .one-c-import-result {
        flex-direction: column;
    }
}

@media (max-width: 420px) {
    .one-c-import-summary {
        grid-template-columns: 1fr;
    }
}
</style>
